import { Audio } from '@remotion/media'
import { useMemo } from 'react'
import { AbsoluteFill, useVideoConfig } from 'remotion'
import { z } from 'zod'
import { Background } from '../components/Background'
import { Subtitles } from '../components/Subtitles'
import { TimelineMedia } from '../components/TimelineMedia'
import { LAYOUTS } from '../layouts'
import { clipScenePropsSchema } from '../types'
import applyTheme from '../utils/applyTheme'
import {
  MediaTimelineContext,
  buildMediaTimeline,
  seamVolume,
} from '../utils/mediaTimeline'
import useFonts from '../utils/useFonts'
import { useSpeaker } from '../utils/useSpeaker'

export type ClipSceneProps = z.infer<typeof clipScenePropsSchema>

export function ClipScene({ clipScene, orientation }: ClipSceneProps) {
  const { fps } = useVideoConfig()
  const layout = LAYOUTS[clipScene.layout]
  const speaker = useSpeaker(clipScene.paragraphs)

  const loaded = useFonts(clipScene.theme)

  const style = applyTheme(layout.defaultStyle, clipScene.theme)

  // One piece per kept segment (#89); one piece, today's trims, without.
  const pieces = useMemo(
    () =>
      buildMediaTimeline({
        mediaStart: clipScene.mediaStart,
        duration: clipScene.duration,
        segments: clipScene.segments,
        fps,
      }),
    [clipScene.mediaStart, clipScene.duration, clipScene.segments, fps],
  )

  if (!loaded) return null

  return (
    <MediaTimelineContext.Provider value={pieces}>
      <AbsoluteFill>
        <Background {...style.background} />

        <layout.Component
          clipScene={clipScene}
          orientation={orientation}
          speaker={speaker}
          style={style}
        >
          <Subtitles
            words={clipScene.words}
            transcriptionStyle={{
              ...style.transcription,
              ...layout.transcriptions[orientation],
            }}
          />
        </layout.Component>

        <TimelineMedia pieces={pieces}>
          {(piece, index) => (
            <Audio
              // disallowFallbackToHtml5Audio
              src={clipScene.mediaUrl}
              trimAfter={piece.trimAfter}
              trimBefore={piece.trimBefore}
              // Dips at each seam, and fades out over the scene's last frames
              // so a window ending mid-word does not pop.
              volume={(f) => seamVolume(pieces, index, f)}
            />
          )}
        </TimelineMedia>
      </AbsoluteFill>
    </MediaTimelineContext.Provider>
  )
}
